import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { ShieldAlert, ArrowRight } from "lucide-react";

/**
 * Compact "Top 10 attackers in last 24h" tile for the admin home.
 *
 * Pulls from /api/admin/system/top-attackers which aggregates rate-limit
 * threshold breaches emitted to MongoDB `security_audit`. Friendly empty state
 * when there's no activity (most days). Inline bar shows bucket mix so the
 * admin can tell at a glance whether they're being credential-stuffed,
 * signup-spammed, or OTP-exhausted.
 */
export default function TopAttackersWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/system/top-attackers?hours=24&limit=10");
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData({ attackers: [], total_breaches_in_window: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  const attackers = data?.attackers || [];
  const total = data?.total_breaches_in_window || 0;

  // Don't take up real-estate when there's no activity — show a tiny "all clear" pill
  if (attackers.length === 0) {
    return (
      <div className="mb-6 p-3 rounded-lg bg-[#DDF0E0] text-[#1F4D2C] text-sm flex items-center gap-2"
           data-testid="top-attackers-allclear">
        <ShieldAlert className="w-4 h-4" />
        Security: no rate-limit threshold breaches in the last 24h — all clear.
      </div>
    );
  }

  const maxTotal = attackers[0]?.total || 1;

  return (
    <div className="mb-6 rounded-lg border border-[#FFD9D9] bg-[#FFF6F6] overflow-hidden"
         data-testid="top-attackers-widget">
      <div className="px-4 py-3 border-b border-[#FFD9D9] flex items-center justify-between bg-[#FFEFEF]">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-[#7A1F1F]" />
          <div>
            <div className="text-sm font-semibold text-[#7A1F1F]">
              Top attackers · last 24h ({attackers.length} unique IPs/phones · {total} total breaches)
            </div>
            <div className="text-xs text-[#7A1F1F]/70">
              Emitted when an IP/phone first crosses a rate-limit threshold.
            </div>
          </div>
        </div>
        <a href="#redis" onClick={(e) => {
          e.preventDefault();
          document.querySelector('[data-testid="admin-redis-tab"]')?.click();
        }}
           className="text-xs text-[#7A1F1F] hover:underline flex items-center gap-1"
           data-testid="top-attackers-link-redis">
          View live throttles <ArrowRight className="w-3 h-3" />
        </a>
      </div>
      <div className="divide-y divide-[#FFE5E5]">
        {attackers.slice(0, 10).map((a, i) => {
          const widthPct = Math.max(8, Math.round((a.total / maxTotal) * 100));
          return (
            <div key={a.identifier} className="px-4 py-2 flex items-center gap-3 text-xs"
                 data-testid={`attacker-row-${i}`}>
              <div className="w-6 text-[#7A1F1F]/60 font-bold">#{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[#1A231C] truncate">{a.identifier}</div>
                {/* Bucket mix bar */}
                <div className="flex h-1.5 mt-1 rounded-full overflow-hidden bg-[#F3F2EE]" style={{ width: `${widthPct}%` }}>
                  {a.login > 0 && <div className="bg-[#D33] h-full" style={{ width: `${(a.login / a.total) * 100}%` }} title={`login: ${a.login}`} />}
                  {a.register > 0 && <div className="bg-[#E59500] h-full" style={{ width: `${(a.register / a.total) * 100}%` }} title={`register: ${a.register}`} />}
                  {a.phoneotp > 0 && <div className="bg-[#1A4480] h-full" style={{ width: `${(a.phoneotp / a.total) * 100}%` }} title={`phone-OTP: ${a.phoneotp}`} />}
                </div>
              </div>
              <div className="text-right whitespace-nowrap">
                <div className="font-bold text-[#7A1F1F]">{a.total} breaches</div>
                <div className="text-[10px] text-[#8A998E]">
                  {a.login > 0 && <span className="mr-1.5"><span className="inline-block w-1.5 h-1.5 bg-[#D33] rounded-full mr-0.5" />login {a.login}</span>}
                  {a.register > 0 && <span className="mr-1.5"><span className="inline-block w-1.5 h-1.5 bg-[#E59500] rounded-full mr-0.5" />register {a.register}</span>}
                  {a.phoneotp > 0 && <span><span className="inline-block w-1.5 h-1.5 bg-[#1A4480] rounded-full mr-0.5" />OTP {a.phoneotp}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
